import React from 'react';
import { Package, ArrowRight, RotateCcw, MapPin } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import RoundTripBadge from '../ui-kit/RoundTripBadge';

/**
 * Bloc de visualisation du trajet d'un transfert (carte aller verte + carte retour grise).
 * Reproduit fidèlement la maquette PM (screenshots 05/05/2026).
 *
 * Props:
 *  - departure: string (adresse)
 *  - arrival: string
 *  - distanceKm: number
 *  - intermediateStops: array [{address, addressComplement}]
 *  - tripType: "one_way" | "round_trip"
 *  - returnMerchandiseDescription: string ("Idem aller (...)" ou désignation propre)
 *  - returnWeight: number
 *  - departureAddressComplement: string
 *  - arrivalAddressComplement: string
 */
const TripDetailsBlock = ({
  departure,
  arrival,
  distanceKm,
  intermediateStops = [],
  tripType = 'one_way',
  returnMerchandiseDescription,
  returnWeight,
  departureAddressComplement,
  arrivalAddressComplement,
}) => {
  const isRoundTrip = tripType === 'round_trip';

  return (
    <div className="space-y-4" data-testid="trip-details-block">
      {/* Carte ALLER (verte) */}
      <Card className="border-emerald-200 overflow-hidden">
        <CardContent className="p-0">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-emerald-100 bg-emerald-50/50">
            <Package className="w-4 h-4 text-emerald-700" />
            <h3 className="text-sm font-semibold text-slate-800">Détails de l'opération</h3>
            {isRoundTrip && (
              <RoundTripBadge variant="full" className="ml-2" />
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 bg-emerald-50/30">
            <div className="p-5 sm:border-r border-emerald-100">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Départ</div>
              <div className="text-sm font-semibold text-slate-800" data-testid="trip-departure">
                {departure || '—'}
              </div>
              {departureAddressComplement && (
                <div className="text-xs text-slate-500 mt-0.5">{departureAddressComplement}</div>
              )}
            </div>
            <div className="p-5 flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center shrink-0 mt-0.5">
                <ArrowRight className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Arrivée</div>
                <div className="text-sm font-semibold text-slate-800" data-testid="trip-arrival">
                  {arrival || '—'}
                </div>
                {arrivalAddressComplement && (
                  <div className="text-xs text-slate-500 mt-0.5">{arrivalAddressComplement}</div>
                )}
              </div>
            </div>
          </div>
          {/* Étapes intermédiaires */}
          {intermediateStops.length > 0 && (
            <div className="px-5 py-3 border-t border-emerald-100 bg-white">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
                Étapes intermédiaires ({intermediateStops.length})
              </div>
              <ul className="space-y-1.5">
                {intermediateStops.map((stop, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-xs text-slate-600">
                    <MapPin className="w-3 h-3 text-slate-400 mt-0.5 shrink-0" />
                    <div>
                      <span className="font-medium text-slate-700">{stop.address || '—'}</span>
                      {stop.addressComplement && (
                        <span className="text-slate-500"> • {stop.addressComplement}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Distance */}
          {distanceKm !== null && distanceKm !== undefined && (
            <div className="px-5 py-2.5 border-t border-emerald-100 bg-emerald-50/40">
              <span className="text-xs text-slate-500">Distance totale : </span>
              <span className="text-sm font-bold text-emerald-700">{distanceKm} km</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Carte RETOUR (grise) — uniquement si aller-retour */}
      {isRoundTrip && (
        <Card className="border-slate-200 overflow-hidden" data-testid="trip-return-block">
          <CardContent className="p-0">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100">
              <RotateCcw className="w-4 h-4 text-slate-600" />
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-700">Trajet retour</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
              <div className="p-5 sm:border-r border-slate-100">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Départ</div>
                <div className="text-sm font-semibold text-slate-800" data-testid="trip-return-departure">
                  {arrival || '—'}
                </div>
              </div>
              <div className="p-5 flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center shrink-0 mt-0.5">
                  <ArrowRight className="w-4 h-4 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Arrivée</div>
                  <div className="text-sm font-semibold text-slate-800" data-testid="trip-return-arrival">
                    {departure || '—'}
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border-t border-slate-100 bg-slate-50/40">
              <div className="p-5 sm:border-r border-slate-100">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Marchandise</div>
                <div className="text-sm text-slate-800">
                  {returnMerchandiseDescription || 'Vide'}
                </div>
              </div>
              <div className="p-5">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Poids retour</div>
                <div className="text-sm font-semibold text-slate-800">
                  {returnWeight ? `${returnWeight} T` : '—'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TripDetailsBlock;
